import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
} from "react";
import { useDispatch } from "react-redux";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Link from "@material-ui/core/Link";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";

import BlockIcon from "@material-ui/icons/Block";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import pageTitle from "../../utils/pageTitle";
import NotFound from "../NotFound";
import {
  TRAVEL_CARD_DETAILS_URL,
  MEDIA_THUMBNAIL_URL,
  PLACEHOLDER_IMG_360x360_URL,
  PLACEHOLDER_IMG_360x360_ERROR_URL,
  IMG_BG_COLOR,
} from "../../config";
import { showErrorAlert } from "../../store/actions/alerts";
import ComponentLoader from "../../components/loader/ComponentLoader";
import StatusChip from "../../components/travel-cards/StatusChip";
import KeyValue from "../../components/display/KeyValue";
import { fromUnixtime, fromIsoOnlyDate } from "../../utils/time";
import ListItem from "../../components/display/ListItem";
import { travelCardUrl } from "../../utils/url";
import { getSpamTypeValue } from "../../utils/helpers";
import UserChip from "../../components/user/UserChip";
import VerifySpamForm from "../../components/spam/VerifySpamForm";
import SuspendActivateForm from "../../components/travel-cards/SuspendActivateForm";
import AdmAreaBreadcrumbs from "../../components/location/AdmAreaBreadcrumbs";

import makeApiRequest from "../../utils/axios/new-axios";
import { permissions, types, checkAcl } from "../../utils/acl";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    overflowX: "auto",
    marginBottom: theme.spacing(2),
    padding: "15px",
  },
  paperDetails: {
    width: "100%",
    overflowX: "auto",
    marginBottom: theme.spacing(1),
    backgroundColor: "#212121",
    padding: "15px",
  },
  img: {
    width: 200,
    height: 110,
    margin: "auto",
    objectFit: "cover",
    backgroundColor: IMG_BG_COLOR,
  },
}));

const ITEM_HEIGHT = 48;

export default function ViewTravelCard(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const EDIT_ACCESS = checkAcl({ p: permissions.TRAVALOUR_USER_POSTS, t: types.VIEW_AND_EDIT });
  const SPAM_EDIT_ACCESS = checkAcl({ p: permissions.SPAM_MANAGEMENT, t: types.VIEW_AND_EDIT });

  const [notFound, setNotFound] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true); //set to true for the initial loading
  const [details, setDetails] = useState(null);
  const [openSpamModal, setOpenSpamModal] = useState(false);
  const [openSuspendModal, setOpenSuspendModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const spamRef = useRef(null);

  const travelCardId = props.match.params.travelCardId;

  // verical menu
  function handleMenuClick(event) {
    setAnchorEl(event.currentTarget);
  }
  function handleMenuClose() {
    setAnchorEl(null);
  }

  //Spam Verify Modal
  function handleOpenSpamModal() {
    setOpenSpamModal(true);
  }
  function handleCloseSpamModal() {
    spamRef.current = null;
    setOpenSpamModal(false);
  }

  //Suspend/Activate User Modal
  function handleOpenSuspendModal() {
    handleMenuClose();
    setOpenSuspendModal(true);
  }
  function handleCloseSuspendModal() {
    setOpenSuspendModal(false);
  }

  useEffect(() => {
    pageTitle("Travelcard");
    fetchTravelCardDetails(travelCardId);
    return () => {
      // axiosInstance.cancel("Component unmounted.");
    };
    // eslint-disable-next-line
  }, []); // [] is required to make sure that it runs only once

  const fetchTravelCardDetails = useCallback(
    async (travelCardId) => {
      try {
        setIsLoadingDetails(true);
        setDetails(null);
        const response = await makeApiRequest({
          url: `${TRAVEL_CARD_DETAILS_URL.url}/${travelCardId}`,
          method: TRAVEL_CARD_DETAILS_URL.method,
        });
        setDetails(response.data);
        pageTitle(response.data.travelCard.name);
        setIsLoadingDetails(false);
      } catch (error) {
        setIsLoadingDetails(false);

        if (!!error.response && error.response.status === 404) {
          setNotFound(true);
        } else {
          dispatch(
            showErrorAlert(
              "Travelcard Fetch Error : " +
              (!!error.response
                ? `${error.response.status}! - ${error.response.data.errorMessage}`
                : error.message)
            )
          );
        }
      }
    },
    [dispatch]
  );

  return (
    <Fragment>
      {!notFound ? (
        <Container style={{ marginTop: "20px", padding: "10px" }}>
          <ComponentLoader isLoading={isLoadingDetails} />
          {!isLoadingDetails && details && details.travelCard && (
            <Fragment>
              <Paper className={classes.paper}>
                <Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    style={{ marginTop: 10 }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {details.travelCard.name}
                    </Typography>

                    <Box>
                      <img
                        className={classes.img}
                        alt=""
                        src={
                          !!details.travelCard.mediaId
                            ? `${MEDIA_THUMBNAIL_URL}/${details.travelCard.mediaId}?width=240`
                            : PLACEHOLDER_IMG_360x360_URL
                        }
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = PLACEHOLDER_IMG_360x360_ERROR_URL;
                        }}
                      />
                    </Box>
                    <Link
                      href={travelCardUrl(
                        details.travelCard.id,
                        details.travelCard.name,
                        true
                      )}
                      underline="none"
                      target="_blank"
                      color="textSecondary"
                      variant="caption"
                    >
                      {`Open in Travalour`}
                    </Link>

                    <Box>
                      <StatusChip status={details.travelCard.status} />
                      {EDIT_ACCESS &&
                        <Fragment>
                          <IconButton
                            aria-label="more"
                            aria-controls="long-menu"
                            aria-haspopup="true"
                            onClick={handleMenuClick}
                            className={classes.menu}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            id="long-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={menuOpen}
                            onClose={handleMenuClose}
                            PaperProps={{
                              style: {
                                maxHeight: ITEM_HEIGHT * 9,
                                width: 200,
                              },
                            }}
                          >
                            <MenuItem
                              key="edit-detail"
                              onClick={handleOpenSuspendModal}
                            >
                              {details.travelCard.status === 1
                                ? "Suspend Travelcard"
                                : "Activate Travelcard"}
                            </MenuItem>
                          </Menu>
                        </Fragment>
                      }
                    </Box>
                  </Box>
                  <Box>
                    <Paper className={classes.paperDetails}>
                      {!!details.travelCard.adm && (
                        <KeyValue
                          keyName="Administrative Area"
                          value={
                            <AdmAreaBreadcrumbs
                              admDetails={details.travelCard.adm}
                            />
                          }
                        />
                      )}
                      <KeyValue
                        keyName="User"
                        value={
                          <UserChip
                            user={{ ...details.travelCard.user, type: 2 }}
                          />
                        }
                      />
                      <KeyValue
                        keyName="Visited On"
                        value={fromIsoOnlyDate(details.travelCard.visitedOn)}
                      />
                      <KeyValue
                        keyName="Created On"
                        value={fromUnixtime(details.travelCard.createdOn)}
                      />
                      <KeyValue
                        keyName="Updated On"
                        value={fromUnixtime(details.travelCard.updatedOn)}
                      />
                      <KeyValue
                        keyName="Location"
                        value={details.travelCard.location.name}
                        link={`/location/${details.travelCard.location.id}`}
                      />
                      <KeyValue
                        keyName="Spam Reports"
                        value={details.travelCard.spamCount}
                      />
                      <KeyValue
                        keyName="Description"
                        value={details.travelCard.description}
                      />
                    </Paper>
                  </Box>
                </Box>
              </Paper>
              {details.spamReports && details.spamReports.length > 0 && (
                <Fragment>
                  <Paper className={classes.paper}>
                    <Typography variant="subtitle1" gutterBottom>
                      Spam Reports
                    </Typography>
                    {details.spamReports.map((spam) => {
                      return (
                        <Paper className={classes.paperDetails} key={spam.id}>
                          <ListItem
                            contentRight={
                              <Fragment>
                                <Box display="flex" alignItems="center">
                                  <BlockIcon
                                    style={{
                                      fontSize: 15,
                                      marginLeft: 5,
                                      marginRight: 5,
                                      color: "#C2C2C2",
                                    }}
                                  />
                                  <Box>
                                    <Typography variant="caption">
                                      {getSpamTypeValue(spam.type)}
                                    </Typography>
                                    {!!spam.reporter.comments && (
                                      <Typography
                                        variant="caption"
                                        style={{ fontStyle: "italic" }}
                                      >
                                        {`  ("${spam.reporter.comments}")`}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <KeyValue
                                  keyName={`Reported On : ${fromUnixtime(
                                    spam.reporter.reportedOn
                                  )}`}
                                  value={
                                    <UserChip
                                      user={{ ...spam.reporter, type: 2 }}
                                    />
                                  }
                                />
                                {!!spam.verifier ? (
                                  <Fragment>
                                    <KeyValue
                                      keyName={`Verified On : ${fromUnixtime(
                                        spam.verifier.verifiedOn
                                      )}`}
                                      value={
                                        <Box>
                                          <UserChip
                                            user={{
                                              ...spam.verifier,
                                              type: 1,
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            display="block"
                                            style={{ fontStyle: "italic" }}
                                          >
                                            {`"${spam.verifier.comments}"`}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                  </Fragment>
                                ) : (
                                  <Fragment>
                                    {SPAM_EDIT_ACCESS &&
                                      <Button
                                        color="secondary"
                                        variant="contained"
                                        size="small"
                                        onClick={() => {
                                          spamRef.current = spam.id;
                                          handleOpenSpamModal();
                                        }}
                                      >
                                        Verify
                                      </Button>}
                                  </Fragment>
                                )}
                              </Fragment>
                            }
                          />
                        </Paper>
                      );
                    })}
                  </Paper>
                  {SPAM_EDIT_ACCESS &&
                    <VerifySpamForm
                      openModal={openSpamModal}
                      handleCloseModal={handleCloseSpamModal}
                      spamId={spamRef.current}
                    />}
                </Fragment>
              )}
              {EDIT_ACCESS &&
                <SuspendActivateForm
                  openModal={openSuspendModal}
                  handleCloseModal={handleCloseSuspendModal}
                  travelCardId={details.travelCard.id}
                  name={details.travelCard.name}
                  status={details.travelCard.status}
                />}
            </Fragment>
          )}
        </Container>
      ) : (
        <NotFound />
      )}
    </Fragment>
  );
}
